import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { Store } from '@ngrx/store';
import { catchError, concatMap, distinctUntilChanged, filter, map, of, skip, switchMap, takeUntil, tap } from 'rxjs';
import { AuthLoginState } from '../../core/auth/auth.models';
import { selectAuthLoginState } from '../../core/auth/auth.selectors';
import { LogService } from '../../core/log/log.service';
import {
  onlyWhenReady,
  onlyWhenOnline,
  //onlyWhenSession
} from '../../shared/util/operators';
import { Content } from '../content/content.model';
import { ContentService } from '../content/content.service';
import {
  AIFA_ID_CATEGORIE,
  ARTICLE_TPL_DESCRIPTION,
  BULLETIN_ID_CATEGORIE,
  CANALI_LOGGATI_ID_RUBRICA,
  CANALI_NON_LOGGATI_ID_RUBRICA,
  FOCUS_MEDICINA_ID_RUBRICA,
  FOCUS_VIDEO_ID_RUBRICA,
  GUIDELINE_ID_CATEGORIE,
  HOME_CONTENT_SECTION2_TOPIC_ID,
  HOME_EXTRA_ID_RUBRICA,
  HOME_HEADER_BANNER_ID_RUBRICA,
  HOME_HIGHLIGHT_ID_RUBRICA,
  HOME_NOT_BE_MISSED_ID_RUBRICA,
  HOME_VIDEO_ID_RUBRICA,
  INSIGHTS_ID_RUBRICA,
  INSTITUTIONS_ID_CATEGORIE,
  MEDLEX_ID_CATEGORIE,
  VIDEO_TPL_DESCRIPTION,
} from './widget.model';
import { manageError, ManageErrorSeverity } from '../error/error.util';
import { selectUserId } from '../user/user.selectors';
import {
  loadAifa,
  loadAifaFailure,
  loadAifaSuccess,
  loadFocus,
  loadFocusListFailure,
  loadFocusListSuccess,
  loadFocusMainFailure,
  loadFocusMainSuccess,
  loadFocusVideo,
  loadFocusVideoListFailure,
  loadFocusVideoListSuccess,
  loadFocusVideoMainFailure,
  loadFocusVideoMainSuccess, loadGuideline, loadGuidelineFailure, loadGuidelineSuccess,
  loadHomeWidgets,
  loadInsights,
  loadInsightsFailure,
  loadInsightsSuccess,
  loadInstitutions,
  loadInstitutionsFailure,
  loadInstitutionsSuccess,
  loadInteresting,
  loadInterestingFailure,
  loadInterestingSuccess,
  loadLatestNews,
  loadLatestNewsFailure,
  loadLatestNewsSuccess,
  loadLatestVideos,
  loadLatestVideosFailure,
  loadLatestVideosSuccess,
  loadMainHighlight,
  loadMainHighlightFailure,
  loadMainHighlightSuccess,
  loadMainHighlightNEW,
  loadMainHighlightNEWFailure,
  loadMainHighlightNEWSuccess,
  loadMedlex,
  loadMedlexFailure,
  loadMedlexSuccess,
  loadMinisterialBulletin,
  loadMinisterialBulletinFailure,
  loadMinisterialBulletinSuccess,
  loadMostRead,
  loadMostReadFailure,
  loadMostReadSuccess,
  loadRelated,
  loadRelatedFailure,
  loadRelatedSuccess,
  loadNotBeMissed, loadNotBeMissedSuccess, loadNotBeMissedFailure,
  loadExtra, loadExtraSuccess, loadExtraFailure,
  loadHomeHero, loadHomeHeroSuccess, loadHomeHeroFailure,
  loadHomeVideo, loadHomeVideoSuccess, loadHomeVideoFailure,
  loadContentHomeSection1, loadContentHomeSection1Success, loadContentHomeSection1Failure,
  loadContentHomeSection2, loadContentHomeSection2Success, loadContentHomeSection2Failure,
  loadHomeHeaderBanner, loadHomeHeaderBannerSuccess, loadHomeHeaderBannerFailure,
  loadRelatedTopic, loadRelatedTopicSuccess, loadRelatedTopicFailure,
  loadRelatedBySpecializzazione, loadRelatedBySpecializzazioneSuccess, loadRelatedBySpecializzazioneFailure,
  clearRelatedBySpecializzazione,
  loadLastContentsByTemplate, loadLastContentsByTemplateSuccess, loadLastContentsByTemplateFailure,
  clearLastContentsByTemplate,
  loadLatestContainers, loadLatestContainersSuccess, loadLatestContainersFailure,
  clearLatestContainers
} from './widget.actions';

@Injectable({ providedIn: 'root' })
export class WidgetEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private contentService: ContentService,
    private logService: LogService
  ) { }

  //#region Home Widgets
  loadHomeWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadHomeWidgets),
      tap((action) => this.logService.info('Effect: loadTopSectionHome', action)),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      concatLatestFrom(() => [
        this.store.select(selectUserId),
        this.store.select(selectAuthLoginState),
      ]),
      concatMap(([, userId, authLoginState]) => {
        const heroSectionID =
          authLoginState === AuthLoginState.LOGGEDGUEST
            ? CANALI_NON_LOGGATI_ID_RUBRICA
            : CANALI_LOGGATI_ID_RUBRICA;
        return [
          loadMainHighlightNEW({ sectionID: HOME_HIGHLIGHT_ID_RUBRICA, userId, fromWhere: 0, howMany: 5 }),
          loadLatestNews({ userId, fromWhere: 0, howMany: 6 }),
          loadNotBeMissed({ sectionID: HOME_NOT_BE_MISSED_ID_RUBRICA, userId, fromWhere: 0, howMany: 1 }),
          loadExtra({ sectionID: HOME_EXTRA_ID_RUBRICA, userId, fromWhere: 0, howMany: 4 }),
          loadHomeHero({ sectionID: heroSectionID, userId, fromWhere: 0, howMany: 6 }),
          loadHomeVideo({ userId, fromWhere: 0, howMany: 3 }),
          loadHomeHeaderBanner({ userId, fromWhere: 0, howMany: 6 }),
          loadContentHomeSection1({ userId, fromWhere: 0, howMany: 3 }),
          loadContentHomeSection2({ topicID: HOME_CONTENT_SECTION2_TOPIC_ID, userId, fromWhere: 0, howMany: 6 }),
        ];
      })
    )
  );
  //#endregion

  //#region Medlex
  loadMedlex$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMedlex),
      tap((action) => this.logService.info('Effect: loadMedlex', action)),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      switchMap(({ userId }) =>
        this.contentService
          .loadContentsCorrelatiByCategorieUrl(
            ARTICLE_TPL_DESCRIPTION,
            MEDLEX_ID_CATEGORIE,
            userId,
            0,
            0,
            1
          )
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadContentMedlex data received: ', response)
            ),
            map((loadedContent: Content[]) => loadMedlexSuccess({ medlexData: loadedContent })),
            catchError((error) => of(loadMedlexFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region Ministerial Bulletin
  loadMinisterialBulletin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMinisterialBulletin),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadMinisterialBulletin', action)),
      switchMap(({ userId }) =>
        this.contentService
          .loadContentsCorrelatiByCategorieUrl(
            ARTICLE_TPL_DESCRIPTION,
            BULLETIN_ID_CATEGORIE,
            userId,
            0,
            0,
            1
          )
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadMinisterialBulletin data received: ', response)
            ),
            map((loadedContent: Content[]) =>
              loadMinisterialBulletinSuccess({
                ministerialBulletinData: loadedContent
              })
            ),
            catchError((error) => of(loadMinisterialBulletinFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region AIFA
  loadAifa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAifa),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadAifa', action)),
      switchMap(({ userId }) =>
        this.contentService
          .loadContentsCorrelatiByCategorieUrl(
            ARTICLE_TPL_DESCRIPTION,
            AIFA_ID_CATEGORIE,
            userId,
            0,
            0,
            1
          )
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadAifa data received: ', response)
            ),
            map((loadedContent: Content[]) => loadAifaSuccess({ aifaData: loadedContent })),
            catchError((error) => of(loadAifaFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region Institutions
  loadIstitutions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInstitutions),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadIstitutions', action)),
      switchMap(({ userId }) =>
        this.contentService
          .loadContentsCorrelatiByCategorieUrl(
            ARTICLE_TPL_DESCRIPTION,
            INSTITUTIONS_ID_CATEGORIE,
            userId,
            0,
            0,
            1
          )
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadIstitutions data received: ', response)
            ),
            map((loadedContent: Content[]) =>
              loadInstitutionsSuccess({ institutionsData: loadedContent })
            ),
            catchError((error) => of(loadInstitutionsFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region Main Highlight
  loadMainHighlight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMainHighlight),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadMainHighlight', action)),
      switchMap(({ userId }) =>
        this.contentService
          .loadHighlightByIdRubricaEvidenza(
            HOME_HIGHLIGHT_ID_RUBRICA,
            0,
            userId,
            ARTICLE_TPL_DESCRIPTION,
            0
          )
          .pipe(
            tap((response: Content) =>
              this.logService.infoDebug('Effect: loadMainHighlight data received: ', response)
            ),
            map((loadedContent: Content) =>
              loadMainHighlightSuccess({ highlightData: loadedContent })
            ),
            catchError((error) => of(loadMainHighlightFailure({ error })))
          )
      )
    )
  );

  loadMainHighlightNEW$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMainHighlightNEW),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadMainHighlightNEW', action)),
      switchMap(({ sectionID, userId, fromWhere, howMany }) =>
        this.contentService.loadHighlightByIdRubricaNEW(sectionID, userId, fromWhere, howMany)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadMainHighlightNEW data received: ', response)
            ),
            map((loadedContent: Content[]) =>
              loadMainHighlightNEWSuccess({ highlightData: loadedContent })
            ),
            catchError((error) => of(loadMainHighlightNEWFailure({ error })))
          )
      )
    )
  )

  //#endregion


  //#region Not Be Missed
  loadNotBeMissed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadNotBeMissed),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadNotBeMissed', action)),
      switchMap(({ sectionID, userId, fromWhere, howMany }) =>
        this.contentService.loadHighlightByIdRubricaNEW(sectionID, userId, fromWhere, howMany)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadNotBeMissed data received: ', response)
            ),
            map((loadedContent: Content[]) =>
              loadNotBeMissedSuccess({ notBeMissedData: loadedContent })
            ),
            catchError((error) => of(loadNotBeMissedFailure({ error })))
          )
      )
    )
  )
  //#endregion

  //#region HomeHero
  loadHomeHero$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadHomeHero),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadHomeHero', action)),
      switchMap(({ sectionID, userId, fromWhere, howMany }) =>
        this.contentService.loadHighlightByIdRubricaNEW(sectionID, userId, fromWhere, howMany)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadHomeHero data received: ', response)
            ),
            map((loadedContent: Content[]) =>
              loadHomeHeroSuccess({ homeHeroData: loadedContent })
            ),
            catchError((error) => of(loadHomeHeroFailure({ error })))
          )
      )
    )
  )

  reloadHomeHeroOnAuthChange$ = createEffect(() =>
    this.store.select(selectAuthLoginState).pipe(
      map((authLoginState) => authLoginState === AuthLoginState.LOGGEDGUEST),
      distinctUntilChanged(),
      skip(1),
      concatLatestFrom(() => this.store.select(selectUserId)),
      filter(([, userId]) => !!userId),
      tap(([isGuest]) =>
        this.logService.info('Effect: reloadHomeHeroOnAuthChange', { isGuest })
      ),
      map(([isGuest, userId]) => {
        const heroSectionID = isGuest
          ? CANALI_NON_LOGGATI_ID_RUBRICA
          : CANALI_LOGGATI_ID_RUBRICA;
        return loadHomeHero({
          sectionID: heroSectionID,
          userId,
          fromWhere: 0,
          howMany: 6,
        });
      })
    )
  )
  //#endregion

  //#region HomeVideo
  loadHomeVideo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadHomeVideo),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadHomeVideo', action)),
      switchMap(({ userId, fromWhere, howMany }) =>
        this.contentService.loadContentList('video', userId, fromWhere, howMany, false)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadHomeVideo data received: ', response)
            ),
            map((loadedContent: Content[]) =>
              loadHomeVideoSuccess({ homeVideoData: loadedContent })
            ),
            catchError((error) => of(loadHomeVideoFailure({ error })))
          )
      )
    )
  )
  //#endregion

  //#region Extra
  loadExtra$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadExtra),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadExtra', action)),
      switchMap(({ sectionID, userId, fromWhere, howMany }) =>
        this.contentService.loadHighlightByIdRubricaNEW(sectionID, userId, fromWhere, howMany)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadExtra data received: ', response)
            ),
            map((loadedContent: Content[]) =>
              loadExtraSuccess({ extraData: loadedContent })
            ),
            catchError((error) => of(loadExtraFailure({ error })))
          )
      )
    )
  )
  //#endregion

  //#region HomeHeaderBanner
  loadHomeHeaderBanner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadHomeHeaderBanner),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadHomeHeaderBanner', action)),
      switchMap(({ userId, fromWhere, howMany }) =>
        this.contentService.loadContentLatestNews(userId, fromWhere, howMany)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadHomeHeaderBanner data received: ', response)
            ),
            map((loadedContent: Content[]) =>
              loadHomeHeaderBannerSuccess({ homeHeaderBannerData: loadedContent })
            ),
            catchError((error) => of(loadHomeHeaderBannerFailure({ error })))
          )
      )
    )
  )
  //#endregion

  //#region ContentHomeSection1
  loadContentHomeSection1$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadContentHomeSection1),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadContentHomeSection1', action)),
      switchMap(({ userId, fromWhere, howMany }) =>
        this.contentService.loadContentLatestByTopic(
          userId, fromWhere, howMany
        ).pipe(
          tap((response: Content[]) =>
            this.logService.infoDebug('Effect: loadContentHomeSection1 data received: ', response)
          ),
          map((loadedContent: Content[]) =>
            loadContentHomeSection1Success({ contentHomeSection1Data: loadedContent })
          ),
          catchError((error) => of(loadContentHomeSection1Failure({ error })))
        )
      )
    )
  )
  //#endregion

  //#region ContentHomeSection2
  loadContentHomeSection2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadContentHomeSection2),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadContentHomeSection2', action)),
      switchMap(({ topicID, userId, fromWhere, howMany }) =>
        this.contentService.loadContentListByCategory(
          topicID, userId, fromWhere, howMany
        ).pipe(
          tap((response: Content[]) =>
            this.logService.infoDebug('Effect: loadContentHomeSection2 data received: ', response)
          ),
          map((loadedContent: Content[]) =>
            loadContentHomeSection2Success({ contentHomeSection2Data: loadedContent })
          ),
          catchError((error) => of(loadContentHomeSection2Failure({ error })))
        )
      )
    )
  )
  //#endregion

  //#region RelatedTopic
  loadRelatedTopic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRelatedTopic),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadRelatedTopic', action)),
      switchMap(({ topicID, userId, fromWhere, howMany }) =>
        this.contentService.loadContentListByCategory(
          topicID, userId, fromWhere, howMany
        ).pipe(
          tap((response: Content[]) =>
            this.logService.infoDebug('Effect: loadRelatedTopic data received: ', response)
          ),
          map((loadedContent: Content[]) =>
            loadRelatedTopicSuccess({ relatedTopicData: loadedContent })
          ),
          catchError((error) => of(loadRelatedTopicFailure({ error })))
        )
      )
    )
  )

  loadRelatedBySpecializzazione$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRelatedBySpecializzazione),
      onlyWhenOnline(this.store),
      // Attesa annullabile: un clear mentre la richiesta e' ancora in coda (sessione non pronta,
      // token in rinnovo) la scarta, invece di lasciarla partire dopo a ripopolare lo slice.
      switchMap((action) =>
        of(action).pipe(
          onlyWhenReady(this.store),
          takeUntil(this.actions$.pipe(ofType(clearRelatedBySpecializzazione)))
        )
      ),
      tap((action) => this.logService.info('Effect: loadRelatedBySpecializzazione', action)),
      switchMap(({ idSpecializzazione, userId, fromWhere, howMany, excludeTemplate }) =>
        this.contentService.loadContentListBySpecializzazione(
          idSpecializzazione, userId, fromWhere, howMany, excludeTemplate
        ).pipe(
          tap((response: Content[]) =>
            this.logService.infoDebug('Effect: loadRelatedBySpecializzazione data received: ', response)
          ),
          map((loadedContent: Content[]) =>
            loadRelatedBySpecializzazioneSuccess({ relatedBySpecializzazioneData: loadedContent })
          ),
          catchError((error) => of(loadRelatedBySpecializzazioneFailure({ error }))),
          // Il clear (ngOnDestroy di dynamic-home) deve annullare anche la richiesta in volo,
          // altrimenti la risposta tardiva ripopola lo slice appena svuotato.
          takeUntil(this.actions$.pipe(ofType(clearRelatedBySpecializzazione)))
        )
      )
    )
  )
  //#endregion

  //#region LastContentsByTemplate
  loadLastContentsByTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLastContentsByTemplate),
      onlyWhenOnline(this.store),
      // Attesa annullabile: vedi loadRelatedBySpecializzazione$.
      switchMap((action) =>
        of(action).pipe(
          onlyWhenReady(this.store),
          takeUntil(this.actions$.pipe(ofType(clearLastContentsByTemplate)))
        )
      ),
      tap((action) => this.logService.info('Effect: loadLastContentsByTemplate', action)),
      switchMap(({ template, idSpecializzazione, userId, fromWhere, howMany }) =>
        this.contentService.loadLastContentsByTemplate(template, idSpecializzazione, userId, fromWhere, howMany).pipe(
          tap((response: Content[]) =>
            this.logService.infoDebug('Effect: loadLastContentsByTemplate data received: ', response)
          ),
          map((loadedContent: Content[]) =>
            loadLastContentsByTemplateSuccess({ lastContentsByTemplateData: loadedContent })
          ),
          catchError((error) => of(loadLastContentsByTemplateFailure({ error }))),
          takeUntil(this.actions$.pipe(ofType(clearLastContentsByTemplate)))
        )
      )
    )
  )
  //#endregion

  //#region LatestContainers
  // Riusa l'endpoint "specializzazionetemplate" con idSpecializzazione = 0: la procedura salta
  // il filtro di specializzazione e, con ord_topic NULL, ordina per data_creazione DESC.
  loadLatestContainers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLatestContainers),
      onlyWhenOnline(this.store),
      // Attesa annullabile: vedi loadRelatedBySpecializzazione$.
      switchMap((action) =>
        of(action).pipe(
          onlyWhenReady(this.store),
          takeUntil(this.actions$.pipe(ofType(clearLatestContainers)))
        )
      ),
      tap((action) => this.logService.info('Effect: loadLatestContainers', action)),
      switchMap(({ template, userId, fromWhere, howMany }) =>
        this.contentService.loadContentListBySpecializzazioneAndTemplate(0, userId, fromWhere, howMany, template).pipe(
          tap((response: Content[]) =>
            this.logService.infoDebug('Effect: loadLatestContainers data received: ', response)
          ),
          map((loadedContent: Content[]) =>
            loadLatestContainersSuccess({ latestContainersData: loadedContent })
          ),
          catchError((error) => of(loadLatestContainersFailure({ error }))),
          takeUntil(this.actions$.pipe(ofType(clearLatestContainers)))
        )
      )
    )
  )
  //#endregion

  //#region Most Read
  loadMostRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMostRead),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadMostRead', action)),
      switchMap(({ userId }) =>
        this.contentService.loadContentMostRead(userId, 0, 4).pipe(
          tap((response: Content[]) =>
            this.logService.infoDebug('Effect: loadMostRead data received: ', undefined, response)
          ),
          map((mostReadData: Content[]) => loadMostReadSuccess({ mostReadData })),
          catchError((error) => of(loadMostReadFailure({ error })))
        )
      )
    )
  );
  //#endregion

  //#region Latest News
  loadLatestNews$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLatestNews),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadLatestNews', action)),
      switchMap((action) =>
        this.contentService
          .loadContentLatestNews(action.userId, action.fromWhere, action.howMany)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug(
                'Effect: loadLatestNews data received: ',
                undefined,
                response
              )
            ),
            map((latestNewsData: Content[]) => loadLatestNewsSuccess({ latestNewsData })),
            catchError((error) => of(loadLatestNewsFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region Related
  loadRelated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRelated),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadRelated', action)),
      switchMap(({ category, template, contentId, userId }) =>
        this.contentService
          .loadContentRelated(category, template, contentId, 93658, userId, 0, 4)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadRelated data received: ', undefined, response)
            ),
            map((relatedData: Content[]) => loadRelatedSuccess({ relatedData })),
            catchError((error) => of(loadRelatedFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region Interesting
  loadInteresting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInteresting),
      onlyWhenOnline(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadInteresting', action)),
      switchMap(({ userId, category, contentId }) =>
        this.contentService
          .loadContentsCorrelatiByCategorieUrl(
            ARTICLE_TPL_DESCRIPTION,
            // INTERESTING_ID_CATEGORIE,
            category,
            userId,
            contentId,
            0,
            4
          )
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug(
                'Effect: loadInteresting data received: ',
                undefined,
                response
              )
            ),
            map((interestingData: Content[]) => loadInterestingSuccess({ interestingData })),
            catchError((error) => of(loadInterestingFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region Latest Videos
  loadLatestVideos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLatestVideos),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadLatestVideos', action)),
      switchMap(({ userId }) =>
        this.contentService.loadContentLatestVideos(userId, 0, 3).pipe(
          tap((contentLatestVideosData: Content[]) =>
            this.logService.infoDebug(
              'Effect: loadLatestVideos data received: ',
              undefined,
              contentLatestVideosData
            )
          ),
          map((latestVideosData: Content[]) => loadLatestVideosSuccess({ latestVideosData })),
          catchError((error) => of(loadLatestVideosFailure({ error })))
        )
      )
    )
  );
  //#endregion

  //#region Focus
  loadFocusMain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFocus),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadFocusMain', action)),
      switchMap(({ userId }) =>
        this.contentService
          .loadHighlightByIdRubricaEvidenza(
            FOCUS_MEDICINA_ID_RUBRICA,
            0,
            userId,
            ARTICLE_TPL_DESCRIPTION,
            0
          )
          .pipe(
            tap((response: Content) =>
              this.logService.infoDebug(
                'Effect: loadFocusMain data received: ',
                undefined,
                response
              )
            ),
            map((focusMainData: Content) => loadFocusMainSuccess({ focusMainData })),
            catchError((error) => of(loadFocusMainFailure({ error })))
          )
      )
    )
  );

  loadFocusList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFocus),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadFocusList', action)),
      switchMap(({ userId }) =>
        this.contentService
          .loadHighlightByIdRubrica(
            FOCUS_MEDICINA_ID_RUBRICA,
            0,
            userId,
            ARTICLE_TPL_DESCRIPTION,
            1,
            4
          )
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug(
                'Effect: loadFocusList data received: ',
                undefined,
                response
              )
            ),
            map((focusListData: Content[]) => loadFocusListSuccess({ focusListData })),
            catchError((error) => of(loadFocusListFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region Focus Video
  loadFocusVideoMain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFocusVideo),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadFocusVideoMain', action)),
      switchMap(({ userId }) =>
        this.contentService
          .loadHighlightByIdRubricaEvidenza(
            FOCUS_VIDEO_ID_RUBRICA,
            0,
            userId,
            VIDEO_TPL_DESCRIPTION,
            0
          )
          .pipe(
            tap((response: Content) =>
              this.logService.infoDebug(
                'Effect: loadFocusVideoMain data received: ',
                undefined,
                response
              )
            ),
            map((focusVideoMainData: Content) => loadFocusVideoMainSuccess({ focusVideoMainData })),
            catchError((error) => of(loadFocusVideoMainFailure({ error })))
          )
      )
    )
  );

  loadFocusVideoList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFocusVideo),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadFocusVideoList', action)),
      switchMap(({ userId }) =>
        this.contentService
          .loadHighlightByIdRubrica(FOCUS_VIDEO_ID_RUBRICA, 0, userId, VIDEO_TPL_DESCRIPTION, 1, 4)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug(
                'Effect: loadFocusVideoList data received: ',
                undefined,
                response
              )
            ),
            map((focusVideoListData: Content[]) =>
              loadFocusVideoListSuccess({ focusVideoListData })
            ),
            catchError((error) => of(loadFocusVideoListFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region Insights
  loadInsights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInsights),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      //onlyWhenSession(this.store),
      tap((action) => this.logService.info('Effect: loadInsights', action)),
      switchMap(({ userId }) =>
        this.contentService
          .loadHighlightByIdRubrica(INSIGHTS_ID_RUBRICA, 0, userId, ARTICLE_TPL_DESCRIPTION, 1, 8)
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadInsights data received: ', undefined, response)
            ),
            map((insightsData: Content[]) => loadInsightsSuccess({ insightsData })),
            catchError((error) => of(loadInsightsFailure({ error })))
          )
      )
    )
  );
  //#endregion

  //#region Guideline
  loadGuideline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadGuideline),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: loadGuidelineGuideline', action)),
      switchMap(({ userId }) =>
        this.contentService.loadContentsCorrelatiByCategorieUrl(
          ARTICLE_TPL_DESCRIPTION,
          GUIDELINE_ID_CATEGORIE,
          userId,
          0,
          0,
          1,
        )
          .pipe(
            tap((response: Content[]) =>
              this.logService.infoDebug('Effect: loadGuideline data received: ', response)
            ),
            map((loadedContent: Content[]) => loadGuidelineSuccess({ guidelineData: loadedContent })),
            catchError((error) => of(loadGuidelineFailure({ error })))
          )
      )
    )
  )
  //#endregion

  //#region Error Handling
  loadWidgetFailures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        loadMedlexFailure,
        loadMinisterialBulletinFailure,
        loadAifaFailure,
        loadInstitutionsFailure,
        loadMostReadFailure,
        loadLatestNewsFailure,
        loadLatestVideosFailure,
        loadMainHighlightFailure,
        loadMainHighlightNEWFailure,
        loadFocusMainFailure,
        loadFocusListFailure,
        loadFocusVideoMainFailure,
        loadFocusVideoListFailure,
        loadInsightsFailure,
        loadInterestingFailure,
        loadRelatedFailure,
        loadGuidelineFailure,
        loadNotBeMissedFailure,
        loadExtraFailure,
        loadHomeHeroFailure,
        loadHomeVideoFailure,
        loadContentHomeSection1Failure,
        loadContentHomeSection2Failure,
        loadHomeHeaderBannerFailure,
        loadRelatedTopicFailure,
        loadRelatedBySpecializzazioneFailure,
        loadLastContentsByTemplateFailure,
        loadLatestContainersFailure,
      ),
      concatLatestFrom(() => this.store.select(selectAuthLoginState)),
      tap(([payload, _]) => this.logService.info('Effect: loadWidgetsFailure', payload.error)),
      // Home widgets are UI furniture: a failure here must not be allowed
      // to demote a real logged-in user. Background severity makes
      // manageError noOp for LOGOUTANDLOGIN/GUESTLOGIN/LOGIN/RENEW classes
      // so a cold-load session-cookie race (very common when pasting a
      // /canale or /minisito URL into a new tab) can't trigger
      // authLogoutAndHard. Critical paths (loadUser, loadDynamicContainer,
      // user-driven actions) still pass Critical and keep demote-on-fail.
      map(([{ error }, authLoginState]) =>
        manageError(error, authLoginState, ManageErrorSeverity.Background)
      )
    )
  );
  //#endregion

}
